import React, { useState, useEffect, useRef } from 'react';
import { Search, Camera, Sparkles, Zap, Tag } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { categories } from '../data/mockData';

const SearchBar = ({ searchQuery, setSearchQuery, onScanBarcode, onPictureSearch, inputRef }) => {
  const { t, tCategory } = useLanguage();
  const [showCategoryHints, setShowCategoryHints] = useState(false);
  const [matchingCategories, setMatchingCategories] = useState([]);
  const wrapperRef = useRef(null);
  
  // Check for matching categories as user types
  useEffect(() => {
    if (searchQuery && searchQuery.length >= 2) {
      const query = searchQuery.toLowerCase().trim();
      const matches = categories.filter(c => 
        c.id !== 'all' && (
          c.id.toLowerCase().includes(query) || 
          c.name.toLowerCase().includes(query)
        )
      );
      setMatchingCategories(matches);
      setShowCategoryHints(matches.length > 0);
    } else {
      setMatchingCategories([]);
      setShowCategoryHints(false);
    }
  }, [searchQuery]);

  // Close hints when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowCategoryHints(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryClick = (categoryId) => {
    setSearchQuery(categoryId);
    setShowCategoryHints(false);
  };
  
  return (
    <div className="mx-4 md:mx-8 lg:mx-16 mt-6 space-y-3">
      {/* Main Search Bar */}
      <div className="flex gap-3">
        <div className="relative flex-1" ref={wrapperRef}>
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#0a1628]/60 z-10" />
          <Input
            ref={inputRef}
            type="text"
            placeholder={t('searchProducts') + " or category (e.g., spices, meats)"}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => matchingCategories.length > 0 && setShowCategoryHints(true)}
            className="w-full pl-12 pr-4 py-6 text-base bg-white border-2 border-[#0a1628]/20 rounded-xl text-[#0a1628] placeholder:text-[#0a1628]/50 focus:ring-2 focus:ring-[#0a1628]/40 focus:border-[#0a1628]/40 shadow-sm hover:shadow-md transition-shadow"
            data-testid="search-input"
          />
          
          {/* Category Hints Dropdown */}
          {showCategoryHints && matchingCategories.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border-2 border-[#0a1628]/20 rounded-xl shadow-lg overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-xs text-gray-500 font-medium flex items-center gap-1">
                  <Tag className="w-3 h-3" /> Browse by category
                </p>
              </div>
              {matchingCategories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => handleCategoryClick(category.id)}
                  className="w-full px-4 py-3 text-left hover:bg-orange-50 flex items-center gap-3 transition-colors"
                  data-testid={`category-hint-${category.id}`}
                >
                  <span className="text-xl">{category.emoji}</span>
                  <div>
                    <p className="font-medium text-[#0a1628]">{tCategory(category.id)}</p>
                    <p className="text-xs text-gray-500">View all {category.name.toLowerCase()}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <Button 
          onClick={onScanBarcode}
          className="bg-[#0a1628] hover:bg-[#0a1628]/90 text-white rounded-xl px-6 py-6 flex items-center gap-2 font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all"
          data-testid="scan-barcode-btn"
        >
          <Camera className="w-5 h-5" />
          <span className="hidden sm:inline">{t('scanBarcode')}</span>
          <span className="sm:hidden">Scan</span>
        </Button>
      </div>

      {/* PICTURES2CART Search Bar */}
      <div 
        onClick={onPictureSearch}
        className="flex items-center gap-3 p-4 bg-white border-2 border-[#0a1628]/30 rounded-xl cursor-pointer hover:border-[#0a1628]/50 hover:shadow-lg hover:shadow-[#0a1628]/10 transition-all group"
        data-testid="picture-search-btn"
      >
        <div className="w-14 h-14 rounded-full bg-[#0a1628] flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-all shadow-lg">
          <Sparkles className="w-7 h-7 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-extrabold text-[#0a1628] flex items-center gap-2 text-lg">
            {t('pictures2cart')}
            <span className="text-xs bg-[#0a1628] text-white px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
              <Zap className="w-3 h-3" /> AI
            </span>
          </h3>
          <p className="text-sm text-[#0a1628]/70 font-medium">
            {t('takePhoto')} • AI • {t('addToCart')}
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-1 bg-[#0a1628] text-white px-4 py-2 rounded-full font-bold shadow-lg group-hover:scale-105 transition-transform">
          <Camera className="w-5 h-5" />
          <span>Try Now</span>
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
